/**
 * Recently viewed products.
 *
 * Tracks viewed product handles in localStorage and hydrates the
 * <recently-viewed> section with real, server-rendered _product-card markup
 * fetched per product via the Section Rendering API. This keeps every card
 * byte-identical to the product-recommendations section, with always-live,
 * market-correct prices.
 */
(function () {
  const STORAGE_KEY = 'ivory_recently_viewed';
  const MAX_STORED = 12;

  /** @returns {string[]} viewed product handles, most recent first */
  function readHandles() {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => (typeof item === 'string' ? item : item && item.handle))
        .filter((handle) => typeof handle === 'string' && handle.length > 0);
    } catch (e) {
      return [];
    }
  }

  /** Records a product view, moving it to the front of the list. */
  function trackView(handle) {
    if (!handle) return;
    try {
      let handles = readHandles().filter((h) => h !== handle);
      handles.unshift(handle);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
    } catch (e) {
      /* localStorage unavailable */
    }
  }

  /** Cache of rendered card nodes for this page session, keyed by handle. */
  const cardCache = new Map();

  /**
   * Fetches one product's recently-viewed section render and extracts the
   * single product card it exposes via <template data-rv-card>.
   * @returns {Promise<Element|null>}
   */
  async function fetchCard(handle, sectionId) {
    if (cardCache.has(handle)) return cardCache.get(handle);

    const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Recently viewed: ${handle} returned ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = doc.querySelector('template[data-rv-card]');
    const card =
      template && template.content.firstElementChild
        ? template.content.firstElementChild.cloneNode(true)
        : null;

    cardCache.set(handle, card);
    return card;
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.currentHandle = this.dataset.currentHandle || '';
      this.sectionId = this.dataset.sectionId || '';
      this.limit = parseInt(this.dataset.limit || '4', 10);

      // Record the current product view before reading the list.
      trackView(this.currentHandle);

      if (this.dataset.rvHydrated) return;

      this.observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          obs.disconnect();
          this.hydrate();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    get inDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    async hydrate() {
      this.dataset.rvHydrated = 'true';

      const handles = readHandles()
        .filter((handle) => handle !== this.currentHandle)
        .slice(0, this.limit);

      if (handles.length < 2) {
        if (!this.inDesignMode) this.removeSection();
        return;
      }

      let cards = [];
      try {
        cards = await Promise.all(
          handles.map((handle) => fetchCard(handle, this.sectionId).catch(() => null))
        );
      } catch (e) {
        cards = [];
      }

      const slides = cards.filter(Boolean);
      if (slides.length < 2) {
        if (!this.inDesignMode) this.removeSection();
        return;
      }

      const trackEl = this.querySelector('[data-rv-track]');
      if (!trackEl) return;
      trackEl.replaceChildren(...slides);

      this.initCarousel(trackEl);
    }

    initCarousel(trackEl) {
      const prev = this.querySelector('[data-rv-prev]');
      const next = this.querySelector('[data-rv-next]');
      if (!prev || !next) return;

      const update = () => {
        const max = trackEl.scrollWidth - trackEl.clientWidth - 1;
        const overflows = max > 0;
        prev.hidden = !overflows;
        next.hidden = !overflows;
        if (!overflows) return;
        prev.disabled = trackEl.scrollLeft <= 0;
        next.disabled = trackEl.scrollLeft >= max;
      };

      const step = (direction) => {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';
        trackEl.scrollBy({ left: direction * trackEl.clientWidth * 0.85, behavior });
      };

      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      trackEl.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }

    removeSection() {
      (this.closest('.shopify-section') || this).remove();
    }
  }

  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewed);
  }
})();
